import { getOwner } from '@ember/application';
import { assert } from '@ember/debug';
import Route from '@ember/routing/route';
// This is for types. Anything we have custom may show an error.
import type { Store as StoreService } from '@warp-drive/core';
import type PartnerService from 'tio-employee/services/partner';
import type SessionContextService from 'tio-employee/services/session-context';
import { OpenSearchQueryBuilder } from 'tio-common/utils/open-search-builder';
import { applyPslfFormTypeFilter } from 'tio-common/utils/pslf-form-type-filter';
import { OpenSearchResponseBuilder } from 'tio-common/utils/open-search-response-builder';
import { executeSearchRequest } from 'tio-data/requests/search';
import { statusesForPslfAdminStatusFilter } from 'tio-employee/utils/pslf/admin-status-filters';
import type { PslfForm, OpenSearchResponse } from 'tio-data/data/resources/search.ext';
import type Transition from '@ember/routing/transition';

type Params = {
  formType: string;
  order: string;
  search: string;
  group: string;
  status: string;
  page: number;
  limit: number;
};
export default class AuthenticatedAdminPslfFormsRoute extends Route {
  get store(): StoreService {
    const owner = getOwner(this);
    assert('Expected owner to be available for route', owner);

    const store = owner.lookup('service:store');
    assert('Expected service:store to be registered', store);
    return store as StoreService;
  }

  get sessionContext(): SessionContextService {
    const owner = getOwner(this);
    assert('Expected owner to be available for route', owner);

    const sessionContext = owner.lookup('service:session-context');
    assert('Expected service:session-context to be registered', sessionContext);
    return sessionContext as SessionContextService;
  }

  get partner(): PartnerService {
    const owner = getOwner(this);
    assert('Expected owner to be available for route', owner);

    const partner = owner.lookup('service:partner');
    assert('Expected service:partner to be registered', partner);
    return partner as PartnerService;
  }

  queryParams = {
    formType: { refreshModel: true, replace: true },
    group: { refreshModel: true, replace: true },
    limit: { refreshModel: true, replace: true },
    page: { refreshModel: true, replace: true },
    search: { refreshModel: true, replace: true },
    order: { refreshModel: true, replace: true },
    status: { refreshModel: true, replace: true },
  };

  async model(params: Params) {
    const { formType, group, order, search, status, page, limit } = params;

    const validatedFormType = formType || '';
    const validatedStatus = status || 'borrower_signed';
    const validatedPage = page || 1;
    const validatedLimit = limit || 25;
    const validatedOrder = order || 'pslf_form_attention_priority_date_date';

    const builder = new OpenSearchQueryBuilder()
      .setSort(validatedOrder)
      .setPagination(validatedPage, validatedLimit)
      .addFilter({
        term: { pslf_form_company_id_keyword: this.sessionContext.currentCompany?.id || -1 },
      });

    const approverGroupIds = this.sessionContext.currentEmployee?.approverPslfGroupIds || [];
    if (approverGroupIds.length > 0) {
      // User is a group approver - restrict to their groups
      if (group) {
        // User selected a specific group - verify they have access
        if (approverGroupIds.includes(group)) {
          builder.mustTerm('pslf_form_employer_group_id_keyword', group);
        } else {
          // User doesn't have access to this group - return empty results
          return { items: [], total: 0, took: 0, isLoading: false };
        }
      } else {
        // No specific group selected - filter to all their approved groups
        builder.mustTerms('pslf_form_employer_group_id_keyword', approverGroupIds);
      }
    } else if (group) {
      // Regular admin with a specific group filter
      builder.mustTerm('pslf_form_employer_group_id_keyword', group);
    }

    if (validatedStatus) {
      builder.mustTermFlexible(
        'pslf_form_status_keyword',
        statusesForPslfAdminStatusFilter(validatedStatus)
      );
    }

    if (validatedFormType) {
      applyPslfFormTypeFilter(builder, validatedFormType);
    } else {
      builder.mustBool('pslf_form_non_partner_boolean', false);
    }

    if (search?.trim()) {
      const tokens = search.trim().split(/\s+/).filter(Boolean);
      builder.setSearchTokens(tokens, ['*'], { fuzziness: 'AUTO' });
    }

    const requestStore = this.store as StoreService & {
      request: (request: unknown) => Promise<unknown>;
    };

    const { content: response } = (await requestStore.request(
      executeSearchRequest({
        'model-type': 'PslfForm',
        'search-body': builder.build(),
      })
    )) as { content: OpenSearchResponse<PslfForm> };

    const responseBuilder = new OpenSearchResponseBuilder<PslfForm>(response);
    return responseBuilder.toPaginatedResult({ includeHighlights: true });
  }

  willTransition(transition: Transition) {
    const watchedParams = ['formType', 'group', 'status', 'search'];

    const from =
      (transition.from as { queryParams?: Record<string, unknown> } | undefined)?.queryParams || {};
    const to =
      (transition.to as { queryParams?: Record<string, unknown> } | undefined)?.queryParams || {};

    const filterChanged = watchedParams.some((param) => (from[param] || '') !== (to[param] || ''));

    if (filterChanged) {
      // eslint-disable-next-line ember/no-controller-access-in-routes
      const controller = this.controllerFor('authenticated.admin.pslf.forms');
      controller.set('page', 1);
    }
    return true;
  }
}
